<script setup lang="ts">
  /**
   * Empty state — 03-components/09c-empty-state · OSCloud DS 1.1
   *
   * What a register shows instead of its rows. It sits INSIDE the container that would have
   * held the content — a table frame or a `BfCard details` section box — and deliberately
   * brings no background, border, radius or shadow of its own: the container it replaces
   * content inside of already has them, and a panel inside a panel reads as a second object.
   *
   * Not a notice (`BfNotice`): that is a message about now, and a permanent one is a message
   * the user learns to ignore. Not `BfConfirmationPanel`: right anatomy, wrong scope — that is
   * a whole-page outcome screen with its own heading rank and centring measure.
   *
   * No modifiers and no tones. Two empty states differ by copy and by the way out, both of
   * which the page owns: `text` (or the default slot) is the sentence, `actions` the way out,
   * `icon` an optional muted disc.
   */
  withDefaults(
    defineProps<{
      /** What is empty. Row rank, not card rank — it stands in for a list of rows. */
      title: string
      /** One sentence saying why. Capped by measure, because it is centred. */
      text?: string
    }>(),
    { text: '' },
  )
</script>

<template>
  <div class="empty-state">
    <span v-if="$slots.icon" class="empty-state__icon" aria-hidden="true">
      <slot name="icon" />
    </span>

    <p class="empty-state__title">{{ title }}</p>

    <p v-if="text || $slots.default" class="empty-state__text">
      <slot>{{ text }}</slot>
    </p>

    <div v-if="$slots.actions" class="empty-state__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .empty-state {
    /* Block-local: the disc is a fixed 44px mark, and the measure is a character cap that no
       spacing token could express. */
    --empty-state-icon-size: 44px;
    --empty-state-measure: 46ch;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--bf-sp-2);
    padding: var(--bf-sp-5) var(--bf-sp-3);
    text-align: center;

    /* A muted disc, not a tone-filled outcome mark: an empty list is neutral — nothing has
       gone wrong. */
    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--empty-state-icon-size);
      height: var(--empty-state-icon-size);
      border-radius: var(--bf-r-pill);
      background: var(--bf-grey-light);
      color: var(--bf-fg-3);
    }

    &__icon :deep(svg) {
      width: 20px;
      height: 20px;
    }

    /* The title is what makes this a state rather than a sentence: it reads at the size and
       weight a row label would have had. */
    &__title {
      margin: 0;
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-base);
      font-weight: var(--bf-fw-semibold);
      color: var(--bf-fg-1);
    }

    /* The sentence is body copy, like the title above it. Stated rather than inherited: the DS
       card relies on a Source Sans 3 page default, and this app's pages sit on the display
       family, so an unstated family renders the sentence in Poppins. */
    &__text {
      margin: 0;
      max-width: var(--empty-state-measure);
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-sm);
      line-height: var(--bf-lh-relaxed);
      color: var(--bf-fg-2);
      text-wrap: pretty;
    }

    /* The way out, at the absence — the reason this block exists rather than a paragraph. */
    &__actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--bf-sp-2);
      margin-top: var(--bf-sp-1);
    }
  }
</style>
